/**
 * Wasiyat reactive store. Minimal pub-sub over a single Vault snapshot.
 * No framework. Components subscribe in connectedCallback, unsubscribe in
 * disconnectedCallback.
 *
 * Mutations go through `mutate(fn)`, which receives a draft, applies the
 * patch, recomputes readiness, persists encrypted envelope, and notifies.
 */

import type { Vault } from './types.ts';
import { computeReadiness, todayISO } from './types.ts';
import { encryptVault, type VaultEnvelope } from './chassis/crypto.ts';
import { saveEnvelope } from './chassis/storage.ts';

export type Phase = 'boot' | 'gate' | 'onboarding' | 'ready' | 'locked';

export interface StoreState {
  phase: Phase;
  vault: Vault | null;
  passcode: string | null; // held in memory only; cleared on lock
  error: string | null;
}

type Listener = (s: StoreState) => void;

class Store {
  #state: StoreState = { phase: 'boot', vault: null, passcode: null, error: null };
  #listeners = new Set<Listener>();
  #pendingPersist: ReturnType<typeof setTimeout> | null = null;

  get state(): Readonly<StoreState> {
    return this.#state;
  }

  patch(p: Partial<StoreState>): void {
    this.#state = { ...this.#state, ...p };
    this.#emit();
  }

  setVault(v: Vault, passcode: string): void {
    this.#state = { phase: 'ready', vault: v, passcode, error: null };
    this.#emit();
  }

  lock(): void {
    this.#state = { phase: 'gate', vault: null, passcode: null, error: null };
    this.#emit();
  }

  async mutate(fn: (draft: Vault) => void): Promise<void> {
    if (!this.#state.vault || !this.#state.passcode) return;
    const draft = structuredClone(this.#state.vault);
    fn(draft);
    draft.generated_at = todayISO();
    draft.readiness = computeReadiness(draft);
    this.#state = { ...this.#state, vault: draft };
    this.#emit();
    this.#schedulePersist();
  }

  subscribe(fn: Listener): () => void {
    this.#listeners.add(fn);
    fn(this.#state);
    return () => this.#listeners.delete(fn);
  }

  #emit(): void {
    for (const fn of this.#listeners) fn(this.#state);
  }

  #schedulePersist(): void {
    if (this.#pendingPersist) clearTimeout(this.#pendingPersist);
    this.#pendingPersist = setTimeout(() => {
      this.#pendingPersist = null;
      void this.#persist();
    }, 400);
  }

  async #persist(): Promise<void> {
    if (!this.#state.vault || !this.#state.passcode) return;
    try {
      const env: VaultEnvelope = await encryptVault(this.#state.passcode, this.#state.vault, null);
      await saveEnvelope(env);
    } catch (e) {
      // Persist failed; surface as non-fatal error
      this.#state = { ...this.#state, error: 'Could not save changes locally.' };
      this.#emit();
    }
  }
}

export const store = new Store();
